import clsx from 'clsx'
import Head from 'next/head'
import { ImageLoaderProps } from 'next/image'
import { CSSProperties, Dispatch, FC, SetStateAction, useState } from 'react'
import useDimensions from 'react-cool-dimensions'
import { useInView } from 'react-intersection-observer'
import s from './Image.module.scss'

type ImgElementWithDataProp = HTMLImageElement & {
  'data-loaded-src': string | undefined
}
type ImageLoader = (props: ImageLoaderProps) => string

type GenImgAttrsData = {
  src: string
  loader: ImageLoader
  width?: number
  unoptimized?: boolean
  quality?: number
  sizes?: string
}

type GenImgAttrsResult = {
  src: string
  srcSet: string | undefined
  sizes: string | undefined
}

export type ImageProps = Omit<
  JSX.IntrinsicElements['img'],
  'src' | 'srcSet' | 'ref' | 'height' | 'loading' | 'width'
> & {
  transparent?: boolean
  alt?: string
  src: string
  id?: string
  width?: number
  height?: number | string
  loader?: ImageLoader
  quality?: number | string
  priority?: boolean
  unoptimized?: boolean
  imgClassName?: string
  rootClassName?: string
  hoverEffect?: boolean
  aspectRatio?: CSSProperties['aspectRatio']
  setIsLoaded?: Dispatch<SetStateAction<boolean>>
  caption?: string
  hideCaption?: boolean

  overwriteCaptionFormatting?: boolean
}

const deviceSizes = [128, 256, 384, 640, 750, 828, 1080, 1200, 1440, 1920]

const Image: FC<ImageProps> = ({
  priority,
  transparent,
  alt,
  src,
  unoptimized,
  width,
  height,
  sizes,
  onLoad,
  imgClassName,
  rootClassName,
  id,
  setIsLoaded,
  hoverEffect = false,
  caption,
  hideCaption,
  overwriteCaptionFormatting,
  aspectRatio,
  ...args
}) => {
  const [loaded, setLoaded] = useState<boolean>(false)
  const [error, setError] = useState<boolean>(false)
  const [inViewRef, inView, entry] = useInView({
    triggerOnce: true,
    threshold: 0,
  })
  const {
    observe,
    width: imageWidth,
    height: imageHeight,
  } = useDimensions<HTMLDivElement | null>()

  const loader = ({ src, width, quality }: ImageLoaderProps): string => {
    if (error || !id) {
      if (src?.includes('{size}'))
        return src.replace('{size}', width.toString())

      if (src?.includes('?')) return src
      return `${src}?anchor=center&mode=crop&width=${width}&height=0&upscale=false&format=webp&quality=${
        quality || 75
      }`
    }

    if (src.endsWith('.gif')) {
      return `https://media.gubi.com/${id}.gif`
    }
    // return `https://${process.env
    //   .NEXT_PUBLIC_CLOUDFRONT_DISTRO!}.cloudfront.net/${id}-${width}.webp`
    return `https://media.gubi.com/${id}-${width}.webp`
  }

  const loadImage = priority === true || inView

  let imgAttributes: GenImgAttrsResult = {
    src: undefined,
    srcSet: undefined,
    sizes: undefined,
  }

  if (loadImage) {
    imgAttributes = generateImgAttrs({
      src,
      unoptimized,
      width,
      sizes: sizes ? sizes : `${imageWidth}px`,
      loader,
    })
  }

  let imageSrcSetPropName = 'imageSrcSet'
  let imageSizesPropName = 'imageSizes'
  if (process.env.__NEXT_REACT_ROOT) {
    imageSrcSetPropName = 'imageSrcSet'
    imageSizesPropName = 'imageSizes'
  }

  const linkProps = {
    // Note: imagesrcset and imagesizes are not in the link element type with react 17.
    [imageSrcSetPropName]: imgAttributes.srcSet,
    [imageSizesPropName]: imgAttributes.sizes,
  }

  return (
    <>
      <div
        className={clsx(s.responsive, rootClassName, {
          [s.transparent]: transparent,
        })}
      >
        <div
          className={clsx(s.observer, { [s.hoverEffect]: hoverEffect })}
          ref={(x) => {
            observe(x)
            inViewRef(x)
          }}
          style={{ aspectRatio }}
        >
          <img
            id={id}
            data-lcp={priority ? 'true' : 'false'}
            {...args}
            {...imgAttributes}
            className={clsx(imgClassName, {
              [s.loaded]: priority || loaded,
            })}
            // ref={React.useCallback((img: ImgElementWithDataProp) => {
            //   inViewRef(img)
            // }, [])}
            onError={(e) => {
              setError(true)
            }}
            onLoad={(event) => {
              setLoaded(true)
              setIsLoaded && setIsLoaded(true)
              if (onLoad) {
                onLoad(event)
              }
            }}
          />
        </div>
        {!hideCaption && caption && (
          <div className="caption">
            {overwriteCaptionFormatting
              ? caption
              : caption.substring(0, 1) +
                caption
                  .toLowerCase()
                  .replace(/([!?.]\s+)([a-z])/g, function (m, $1, $2) {
                    return $1 + $2.toUpperCase()
                  })
                  .substring(1)}
          </div>
        )}
      </div>
      {priority ? (
        // Note how we omit the `href` attribute, as it would only be relevant
        // for browsers that do not support `imagesrcset`, and in those cases
        // it would likely cause the incorrect image to be preloaded.
        //
        // https://html.spec.whatwg.org/multipage/semantics.html#attr-link-imagesrcset
        <Head>
          <link
            key={
              '__nimg-' +
              imgAttributes.src +
              imgAttributes.srcSet +
              imgAttributes.sizes
            }
            rel="preload"
            as="image"
            href={imgAttributes.srcSet ? undefined : imgAttributes.src}
            {...linkProps}
          />
        </Head>
      ) : null}
    </>
  )
}

function generateImgAttrs({
  src,
  width,
  quality,
  unoptimized,
  sizes,
  loader,
}: GenImgAttrsData): GenImgAttrsResult {
  if (unoptimized) {
    return { src, srcSet: undefined, sizes: undefined }
  }

  const { widths, kind } = getWidths(width, sizes)
  const last = widths.length - 1
  return {
    sizes: !sizes && kind === 'w' ? '100vw' : sizes,
    srcSet: widths
      .map(
        (w, i) =>
          `${loader({ src, quality, width: w })} ${
            kind === 'w' ? w : i + 1
          }${kind}`
      )
      .join(', '),

    // It's intended to keep `src` the last attribute because React updates
    // attributes in order. If we keep `src` the first one, Safari will
    // immediately start to fetch `src`, before `sizes` and `srcSet` are even
    // updated by React. That causes multiple unnecessary requests if `srcSet`
    // and `sizes` are defined.
    // This bug cannot be reproduced in Chrome or Firefox.
    src: loader({ src, quality, width: widths[last] }),
  }
}
function getWidths(
  width: number | undefined,
  sizes: string | undefined
): { widths: number[]; kind: 'w' | 'x' } {
  if (sizes) {
    // Find all the "vw" percent sizes used in the sizes prop
    const viewportWidthRe = /(^|\s)(1?\d?\d)vw/g
    const percentSizes = []
    for (let match; (match = viewportWidthRe.exec(sizes)); match) {
      percentSizes.push(parseInt(match[2]))
    }
    if (percentSizes.length) {
      const smallestRatio = Math.min(...percentSizes) * 0.01
      return {
        widths: deviceSizes.filter((s) => s >= deviceSizes[0] * smallestRatio),
        kind: 'w',
      }
    }
    return { widths: deviceSizes, kind: 'w' }
  }
  if (typeof width !== 'number') {
    return { widths: deviceSizes, kind: 'w' }
  }

  const widths = [
    //@ts-ignore
    ...new Set(
      // > This means that most OLED screens that say they are 3x resolution,
      // > are actually 3x in the green color, but only 1.5x in the red and
      // > blue colors. Showing a 3x resolution image in the app vs a 2x
      // > resolution image will be visually the same, though the 3x image
      // > takes significantly more data. Even true 3x resolution screens are
      // > wasteful as the human eye cannot see that level of detail without
      // > something like a magnifying glass.
      // https://blog.twitter.com/engineering/en_us/topics/infrastructure/2019/capping-image-fidelity-on-ultra-high-resolution-devices.html
      [width, width * 2 /*, width * 3*/].map(
        (w) =>
          deviceSizes.find((p) => p >= w) || deviceSizes[deviceSizes.length - 1]
      )
    ),
  ]
  return { widths, kind: 'x' }
}

export default Image
